import { Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { LandingPage } from './components/LandingPage'
import { useAuthStore } from './stores/authStore'
import { apiClient } from './services/api'

const Dashboard = lazy(() => import('./components/AppDashboard'))
const SharePage = lazy(() => import('./components/SharePage').then((module) => ({ default: module.SharePage })))
const AuthScreen = lazy(() => import('./components/AuthScreen').then((module) => ({ default: module.AuthScreen })))
const AdminCenter = lazy(() => import('./components/AdminCenter'))

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5] text-[#787774] text-sm">
      页面加载中...
    </div>
  )
}

function LegacyEvalsRedirect() {
  const location = useLocation()
  return <Navigate to={`/admin${location.search || ''}`} replace />
}

function AppRoutes() {
  const { isAuthenticated, accessToken, logout, updateTokens } = useAuthStore()
  const [showLogin, setShowLogin] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handleLogout = (event: CustomEvent) => {
      console.log('Auth logout event:', event.detail)
      logout()
      setShowLogin(true)
      navigate('/')
    }

    window.addEventListener('auth:logout', handleLogout as EventListener)
    return () => {
      window.removeEventListener('auth:logout', handleLogout as EventListener)
    }
  }, [logout, navigate])

  useEffect(() => {
    if (!accessToken) return

    const checkAndRefreshToken = async () => {
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) return

      try {
        const currentAccessToken = localStorage.getItem('access_token')
        if (!currentAccessToken) return

        await apiClient.getProfile()
      } catch (error: any) {
        if (error.response?.status !== 401) {
          console.debug('Token check failed:', error)
        }
      }
    }

    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000)
    checkAndRefreshToken()

    return () => clearInterval(interval)
  }, [accessToken, updateTokens, logout, navigate])

  const handleLogout = async () => {
    try {
      await apiClient.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      logout()
      navigate('/')
    }
  }

  const handleAuthSuccess = () => {
    setShowLogin(false)
    navigate('/app')
  }

  const isLoggedIn = isAuthenticated && !!accessToken

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={<LandingPage onLogin={() => setShowLogin(true)} isAuthenticated={isLoggedIn} />}
        />
        <Route
          path="/app"
          element={
            isLoggedIn ? (
              <Suspense fallback={<RouteFallback />}>
                <Dashboard onLogout={handleLogout} />
              </Suspense>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/s/:shareId"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SharePage />
            </Suspense>
          }
        />
        <Route
          path="/admin"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AdminCenter />
            </Suspense>
          }
        />
        <Route path="/evals" element={<LegacyEvalsRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showLogin && (
        <Suspense fallback={null}>
          <AuthScreen onAuthSuccess={handleAuthSuccess} onBack={() => setShowLogin(false)} />
        </Suspense>
      )}
    </>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  )
}

export default App
